import React, { useState, useEffect, useCallback } from 'react';
import {
  Settings,
  CreditCard,
  Save,
  RotateCcw,
  CheckCircle2,
  AlertCircle,
  Receipt,
  Percent,
  Network,
  Clock,
  ArrowRight,
  RefreshCw,
  XCircle,
  Link2,
  Loader2,
  Activity,
  Key,
  ShieldAlert,
  Unlink
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/lib/api';

const ALLOWED_PAYMENT_MODES = [
  { value: 'NONE', label: 'None (Require selection every time)' },
  { value: 'CASH', label: 'Cash (CASH)' },
  { value: 'CARD', label: 'Card (CARD)' },
  { value: 'UPI', label: 'UPI (UPI)' },
  { value: 'NET_BANKING', label: 'Net Banking (NET_BANKING)' },
];

export const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Payment Mode Preference State
  const [selectedMode, setSelectedMode] = useState('NONE');
  const [savedMode, setSavedMode] = useState(null);

  // Tax Override & Discount Preferences State
  const [overrideErpTax, setOverrideErpTax] = useState(true);
  const [savedOverrideErpTax, setSavedOverrideErpTax] = useState(true);
  const [useErpProductTax, setUseErpProductTax] = useState(false);
  const [savedUseErpProductTax, setSavedUseErpProductTax] = useState(false);
  const [prescriptionDiscount, setPrescriptionDiscount] = useState('0');
  const [savedPrescriptionDiscount, setSavedPrescriptionDiscount] = useState('0');
  const [discountError, setDiscountError] = useState('');

  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/settings');
      const data = res.data?.data || res.data || {};

      const mode = data.default_payment_mode || 'NONE';
      setSelectedMode(mode);
      setSavedMode(data.default_payment_mode || null);

      const taxOverride = data.use_erp_product_tax !== undefined
        ? !Boolean(data.use_erp_product_tax)
        : (data.override_erp_tax !== undefined ? Boolean(data.override_erp_tax) : true);
      setOverrideErpTax(taxOverride);
      setSavedOverrideErpTax(taxOverride);
      setUseErpProductTax(!taxOverride);
      setSavedUseErpProductTax(!taxOverride);

      const discountVal = data.prescription_discount_percent !== undefined
        ? String(data.prescription_discount_percent)
        : '0';
      setPrescriptionDiscount(discountVal);
      setSavedPrescriptionDiscount(discountVal);
      setDiscountError('');

      if (data.updated_at) {
        setLastUpdated({
          at: data.updated_at,
          by: data.updated_by_name || 'Admin',
        });
      }
    } catch (err) {
      const errMsg = err.response?.data?.detail || err.response?.data?.message || 'Failed to load clinic settings';
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // ERP Integration Onboarding State
  const [erpStatus, setErpStatus] = useState(null);
  const [erpLoading, setErpLoading] = useState(false);
  const [erpActionLoading, setErpActionLoading] = useState(false);
  const [erpTestLoading, setErpTestLoading] = useState(false);
  const [erpTestResult, setErpTestResult] = useState(null);
  const isPollingRef = React.useRef(false);

  const fetchErpStatus = useCallback(async (isManual = false) => {
    if (isPollingRef.current && !isManual) return;
    isPollingRef.current = true;
    if (isManual) setErpLoading(true);
    try {
      const res = await api.get('/api/admin/erp/status');
      const data = res.data?.data || res.data || {};
      setErpStatus(data);
    } catch (err) {
      if (isManual) {
        toast.error('Failed to retrieve ERP integration status');
      }
    } finally {
      isPollingRef.current = false;
      if (isManual) setErpLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchErpStatus();

    // Check URL parameters for feedback
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('erp_connected') === 'true') {
      toast.success('Successfully connected to OneSpace ERP! Real-time synchronization active.');
      window.history.replaceState({}, '', window.location.pathname);
      fetchErpStatus(true);
    } else if (urlParams.get('erp_oauth_error')) {
      toast.error(`OneSpace ERP authorization failed: ${urlParams.get('erp_oauth_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
      fetchErpStatus(true);
    } else if (urlParams.get('erp_request') === 'submitted') {
      toast.success('OneSpace ERP integration request registered! Awaiting Platform Admin review.');
      window.history.replaceState({}, '', window.location.pathname);
      fetchErpStatus(true);
    } else if (urlParams.get('erp_error')) {
      toast.error(`Integration request error: ${urlParams.get('erp_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }

    // 60-second active polling interval when pending approval
    const intervalId = setInterval(() => {
      if (erpStatus?.status === 'PENDING_APPROVAL') {
        fetchErpStatus();
      }
    }, 60000);

    // 15-minute background polling interval as general fallback
    const bgIntervalId = setInterval(() => {
      fetchErpStatus();
    }, 900000);

    return () => {
      clearInterval(intervalId);
      clearInterval(bgIntervalId);
    };
  }, [fetchErpStatus, erpStatus?.status]);

  const handleInitiateErpRequest = async () => {
    setErpActionLoading(true);
    try {
      const res = await api.post('/api/admin/erp/initiate-request');
      const redirectUrl = res.data?.data?.redirect_url;
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        toast.error('Failed to generate OneSpace ERP request redirect URL');
        setErpActionLoading(false);
      }
    } catch (err) {
      const errMsg = err.response?.data?.detail || err.response?.data?.message || 'Failed to initiate ERP integration request';
      toast.error(errMsg);
      setErpActionLoading(false);
    }
  };

  const handleCancelErpRequest = async () => {
    if (!window.confirm('Are you sure you want to cancel this pending OneSpace ERP integration request?')) return;
    setErpActionLoading(true);
    try {
      await api.post('/api/admin/erp/cancel');
      toast.success('Integration request cancelled.');
      fetchErpStatus(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel request');
    } finally {
      setErpActionLoading(false);
    }
  };

  const handleResetErpConnection = async () => {
    setErpActionLoading(true);
    try {
      await api.post('/api/admin/erp/reset');
      fetchErpStatus(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset request status');
    } finally {
      setErpActionLoading(false);
    }
  };

  const handleInitiateErpOAuth = async () => {
    setErpActionLoading(true);
    try {
      const res = await api.post('/api/admin/erp/oauth/initiate');
      const redirectUrl = res.data?.data?.redirect_url;
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        toast.error('Failed to generate OneSpace ERP OAuth consent URL');
        setErpActionLoading(false);
      }
    } catch (err) {
      const errMsg = err.response?.data?.detail || err.response?.data?.message || 'Failed to initiate OAuth consent flow';
      toast.error(errMsg);
      setErpActionLoading(false);
    }
  };

  const handleTestErpConnection = async () => {
    setErpTestLoading(true);
    setErpTestResult(null);
    try {
      const res = await api.post('/api/admin/erp/test-connection');
      const data = res.data?.data;
      const isUp = data?.status === 'UP' || data?.status === 'OK' || data?.success;
      setErpTestResult(data || { status: 'UP', message: 'Connection healthy' });
      if (isUp) {
        toast.success('OneSpace ERP connection verified successfully!');
      } else {
        toast.warning(data?.error || 'ERP connection returned degraded status');
      }
    } catch (err) {
      const errMsg = err.response?.data?.detail || err.response?.data?.message || 'ERP connection test failed';
      setErpTestResult({ status: 'DOWN', message: errMsg });
      toast.error(`ERP Test Failed: ${errMsg}`);
    } finally {
      setErpTestLoading(false);
    }
  };

  const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
  const [rotateModalOpen, setRotateModalOpen] = useState(false);
  const [disconnectReason, setDisconnectReason] = useState('');
  const [rotateReason, setRotateReason] = useState('');

  const handleDisconnectErp = async (e) => {
    if (e) e.preventDefault();
    setErpActionLoading(true);
    try {
      await api.post('/api/admin/erp/disconnect', { reason: disconnectReason.trim() || 'Manual clinic admin disconnect' });
      toast.success('OneSpace ERP connection disconnected and revoked successfully.');
      setDisconnectModalOpen(false);
      setDisconnectReason('');
      fetchErpStatus(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disconnect ERP connection');
    } finally {
      setErpActionLoading(false);
    }
  };

  const handleRotateErpCredentials = async (e) => {
    if (e) e.preventDefault();
    setErpActionLoading(true);
    try {
      await api.post('/api/admin/erp/rotate-credentials', { reason: rotateReason.trim() || 'Scheduled clinic rotation' });
      toast.success('OneSpace ERP credentials rotated and verified successfully!');
      setRotateModalOpen(false);
      setRotateReason('');
      fetchErpStatus(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to rotate ERP credentials');
    } finally {
      setErpActionLoading(false);
    }
  };

  const handleDiscountChange = (e) => {
    const val = e.target.value;
    setPrescriptionDiscount(val);
    if (val === '') {
      setDiscountError('Prescription discount percentage is required (0 to 100).');
      return;
    }
    const num = parseFloat(val);
    if (isNaN(num)) {
      setDiscountError('Please enter a valid numeric percentage.');
    } else if (num < 0 || num > 100) {
      setDiscountError('Discount percentage must be between 0 and 100.');
    } else {
      setDiscountError('');
    }
  };

  const handleSave = async () => {
    const numDiscount = parseFloat(prescriptionDiscount);
    if (isNaN(numDiscount) || numDiscount < 0 || numDiscount > 100) {
      toast.error('Prescription discount percent must be between 0 and 100.');
      setDiscountError('Discount percentage must be between 0 and 100.');
      return;
    }

    setSaving(true);
    try {
      const modeToSend = selectedMode === 'NONE' ? null : selectedMode;
      const res = await api.put('/api/admin/settings', {
        default_payment_mode: modeToSend,
        use_erp_product_tax: !overrideErpTax,
        override_erp_tax: overrideErpTax,
        prescription_discount_percent: numDiscount,
      });
      const data = res.data?.data || res.data || {};

      const newMode = data.default_payment_mode || null;
      setSavedMode(newMode);
      setSelectedMode(newMode || 'NONE');

      const newTaxOverride = data.use_erp_product_tax !== undefined
        ? !Boolean(data.use_erp_product_tax)
        : (data.override_erp_tax !== undefined ? Boolean(data.override_erp_tax) : true);
      setSavedOverrideErpTax(newTaxOverride);
      setOverrideErpTax(newTaxOverride);
      setSavedUseErpProductTax(!newTaxOverride);
      setUseErpProductTax(!newTaxOverride);

      const newDiscount = data.prescription_discount_percent !== undefined
        ? String(data.prescription_discount_percent)
        : '0';
      setSavedPrescriptionDiscount(newDiscount);
      setPrescriptionDiscount(newDiscount);
      setDiscountError('');

      if (data.updated_at) {
        setLastUpdated({
          at: data.updated_at,
          by: data.updated_by_name || 'Admin',
        });
      }
      toast.success('Clinic settings and ERP billing preferences updated successfully.');
    } catch (err) {
      const errMsg = err.response?.data?.detail || err.response?.data?.message || 'Failed to update clinic settings';
      toast.error(errMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    setSelectedMode('NONE');
  };

  const handleReset = () => {
    setSelectedMode(savedMode || 'NONE');
    setOverrideErpTax(savedOverrideErpTax);
    setPrescriptionDiscount(savedPrescriptionDiscount);
    setDiscountError('');
  };

  const isDirty =
    (savedMode || 'NONE') !== selectedMode ||
    savedOverrideErpTax !== overrideErpTax ||
    (parseFloat(savedPrescriptionDiscount || '0') !== parseFloat(prescriptionDiscount || '0'));

  return (
    <div className="space-y-6 max-w-4xl mx-auto py-2">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b pb-5">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2.5">
            <Settings className="h-6 w-6 text-emerald-700" />
            Clinic Settings
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Configure clinic-wide operational preferences, ERP billing rules, and prescription defaults.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {savedMode ? (
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 font-medium">
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
              Default: {savedMode}
            </Badge>
          ) : (
            <Badge variant="outline" className="text-slate-600 border-slate-300">
              No Payment Default
            </Badge>
          )}
          <Badge variant="outline" className="text-slate-700 border-slate-300 bg-white">
            Tax Override: {savedOverrideErpTax ? 'ON (0%)' : 'OFF (ERP GST)'}
          </Badge>
          {parseFloat(savedPrescriptionDiscount || '0') > 0 && (
            <Badge variant="outline" className="text-emerald-700 border-emerald-300 bg-emerald-50 font-medium">
              Discount: {savedPrescriptionDiscount}%
            </Badge>
          )}
        </div>
      </div>

      {loading ? (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="py-12 text-center text-sm text-slate-500">
            Loading clinic settings...
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* OneSpace ERP Integration Card */}
          <Card className="border-slate-200 shadow-sm" data-testid="erp-integration-card">
            <CardHeader className="pb-4 border-b bg-slate-50/50 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <Network className="h-5 w-5 text-emerald-700" />
                  OneSpace ERP Integration
                </CardTitle>
                <CardDescription className="text-xs text-slate-500">
                  Automated two-stage onboarding with OneSpace ERP for real-time inventory and sales sync.
                </CardDescription>
              </div>
              <div>
                {erpStatus?.status === 'ACTIVE' && (
                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Connected
                  </Badge>
                )}
                {erpStatus?.status === 'PENDING_APPROVAL' && (
                  <Badge className="bg-amber-100 text-amber-800 border-amber-300">
                    <Clock className="w-3.5 h-3.5 mr-1" /> Request Pending
                  </Badge>
                )}
                {erpStatus?.status === 'APPROVED' && (
                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Request Approved
                  </Badge>
                )}
                {erpStatus?.status === 'REJECTED' && (
                  <Badge className="bg-red-100 text-red-800 border-red-300">
                    <AlertCircle className="w-3.5 h-3.5 mr-1" /> Request Rejected
                  </Badge>
                )}
                {erpStatus?.status === 'CANCELLED' && (
                  <Badge className="bg-slate-100 text-slate-800 border-slate-300">
                    Cancelled
                  </Badge>
                )}
                {(!erpStatus || erpStatus?.status === 'NOT_REQUESTED') && (
                  <Badge variant="outline" className="text-slate-500 border-slate-300">
                    Not Connected
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              {/* State NOT_REQUESTED */}
              {(!erpStatus || erpStatus?.status === 'NOT_REQUESTED') && (
                <div className="space-y-4">
                  <div className="rounded-lg bg-emerald-50/60 p-4 border border-emerald-200 text-xs text-emerald-900 space-y-1">
                    <p className="font-semibold text-sm text-emerald-950">Automated Two-Stage Onboarding</p>
                    <p>
                      Connect AyurClinic directly to your OneSpace ERP account for real-time inventory synchronization and automated billing.
                    </p>
                    <p className="text-emerald-800 pt-1">
                      <strong>Step 1:</strong> Submit your request to connect your OneSpace ERP subscription. The request is vetted by OneSpace within 24–48 hours.
                    </p>
                    <p className="text-emerald-800">
                      <strong>Step 2:</strong> Authorize permissions securely through your OneSpace administrator account.
                    </p>
                  </div>
                  <div>
                    <Button
                      onClick={handleInitiateErpRequest}
                      disabled={erpActionLoading}
                      className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2 text-xs"
                    >
                      {erpActionLoading ? (
                        <>
                          <RefreshCw className="h-4 w-4 animate-spin" />
                          Initiating...
                        </>
                      ) : (
                        <>
                          <ArrowRight className="h-4 w-4" />
                          Request OneSpace ERP Integration
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* State PENDING_APPROVAL */}
              {erpStatus?.status === 'PENDING_APPROVAL' && (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 space-y-1.5">
                    <div className="flex items-center gap-2 font-semibold text-sm text-amber-950">
                      <Clock className="w-4 h-4 text-amber-600" />
                      Request Submitted — Awaiting Platform Review
                    </div>
                    <p>
                      Your integration request has been received. OneSpace Platform Administrators review tenant eligibility and active subscription status within <strong>24–48 hours</strong>.
                    </p>
                    <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] font-mono text-amber-800">
                      <div>Request ID: <span className="font-bold text-amber-950">{erpStatus.requestId}</span></div>
                      <div>Submitted: <span>{erpStatus.createdAt ? new Date(erpStatus.createdAt).toLocaleString() : 'N/A'}</span></div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchErpStatus(true)}
                      disabled={erpLoading || erpActionLoading}
                      className="text-xs gap-1.5"
                    >
                      <RefreshCw className={`h-3.5 w-3.5 ${erpLoading ? 'animate-spin' : ''}`} />
                      Check Status
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCancelErpRequest}
                      disabled={erpActionLoading}
                      className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      Cancel Request
                    </Button>
                  </div>
                </div>
              )}

              {/* State APPROVED */}
              {erpStatus?.status === 'APPROVED' && (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-900 space-y-1.5">
                    <div className="flex items-center gap-2 font-semibold text-sm text-emerald-950">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      Connection Request Approved!
                    </div>
                    <p>
                      Your organization has been vetted and approved for OneSpace ERP integration.
                    </p>
                    <div className="pt-1 text-[11px] text-emerald-800">
                      Reviewed at: <span>{erpStatus.reviewedAt ? new Date(erpStatus.reviewedAt).toLocaleString() : 'Recently'}</span>
                    </div>
                  </div>
                  <div>
                    <Button
                      onClick={handleInitiateErpOAuth}
                      disabled={erpActionLoading}
                      className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2 text-xs"
                      data-testid="connect-erp-button"
                    >
                      {erpActionLoading ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          Initiating Authorization...
                        </>
                      ) : (
                        <>
                          <Link2 className="h-3.5 w-3.5" />
                          Connect OneSpace ERP
                        </>
                      )}
                    </Button>
                    <p className="text-[11px] text-slate-500 mt-1.5">
                      Redirects to OneSpace consent screen to grant permissions and establish connection.
                    </p>
                  </div>
                </div>
              )}

              {/* State ACTIVE */}
              {erpStatus?.status === 'ACTIVE' && (
                <div className="space-y-4" data-testid="erp-connected-section">
                  <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-900 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 font-semibold text-sm text-emerald-950">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        Connected to OneSpace ERP
                      </div>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-800 border border-emerald-300">
                        Connected
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
                      <div>
                        <span className="text-slate-500 font-medium">Company Name:</span>{' '}
                        <span className="font-semibold text-slate-800">{erpStatus.companyName || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-slate-500 font-medium">Company ID:</span>{' '}
                        <span className="font-mono font-semibold text-slate-800">{erpStatus.companyId || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-slate-500 font-medium">Client ID:</span>{' '}
                        <span className="font-mono text-slate-800">{erpStatus.clientId || 'ayurclinic'}</span>
                      </div>
                      <div>
                        <span className="text-slate-500 font-medium">Connected Since:</span>{' '}
                        <span className="text-slate-800">{erpStatus.connectedAt ? new Date(erpStatus.connectedAt).toLocaleString() : 'Active'}</span>
                      </div>
                      <div className="col-span-1 md:col-span-2">
                        <span className="text-slate-500 font-medium">Security & Storage:</span>{' '}
                        <span className="text-slate-700">AES-256-GCM Encrypted at Rest (Zero-Restart Dynamic Credential)</span>
                      </div>
                    </div>

                    <div className="pt-2 border-t border-emerald-200">
                      <div className="text-[11px] font-medium text-slate-600 mb-1.5">Authorized Scopes:</div>
                      <div className="flex flex-wrap gap-1.5">
                        {(erpStatus.grantedScopes ? erpStatus.grantedScopes.split(',') : ['products:read', 'batches:read', 'sales:create', 'sales:read']).map((scope) => (
                          <Badge key={scope.trim()} variant="outline" className="bg-white/80 text-emerald-800 border-emerald-300 font-mono text-[10px]">
                            {scope.trim()}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Test Connection Button & Indicator */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-1">
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={handleTestErpConnection}
                        disabled={erpTestLoading}
                        variant="outline"
                        className="text-xs gap-1.5 border-slate-300 hover:bg-slate-50"
                        data-testid="test-erp-connection-button"
                      >
                        {erpTestLoading ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Activity className="h-3.5 w-3.5 text-emerald-600" />
                        )}
                        Test Connection
                      </Button>
                      <Button
                        onClick={() => fetchErpStatus(true)}
                        disabled={erpLoading || erpActionLoading}
                        variant="ghost"
                        size="sm"
                        className="h-8 text-xs text-slate-500 hover:text-slate-700"
                        title="Refresh status"
                      >
                        <RefreshCw className={`h-3.5 w-3.5 ${erpLoading ? 'animate-spin' : ''}`} />
                      </Button>
                    </div>

                    {erpTestResult && (
                      <div className={`text-xs px-2.5 py-1 rounded border flex items-center gap-1.5 ${
                        erpTestResult.status === 'UP' || erpTestResult.status === 'OK' || erpTestResult.success
                          ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                          : 'bg-red-50 text-red-800 border-red-200'
                      }`}>
                        {erpTestResult.status === 'UP' || erpTestResult.status === 'OK' || erpTestResult.success ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                        ) : (
                          <AlertCircle className="h-3.5 w-3.5 text-red-600" />
                        )}
                        <span>{erpTestResult.message || `Status: ${erpTestResult.status || 'Active'}`}</span>
                      </div>
                    )}
                  </div>

                  {/* Phase 3 Action Buttons */}
                  <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-emerald-100">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setRotateReason('');
                        setRotateModalOpen(true);
                      }}
                      disabled={erpActionLoading}
                      className="text-xs text-amber-800 border-amber-300 hover:bg-amber-50 gap-1.5"
                      data-testid="rotate-credentials-button"
                    >
                      <Key className="w-3.5 h-3.5 text-amber-600" />
                      Rotate Credentials
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setDisconnectReason('');
                        setDisconnectModalOpen(true);
                      }}
                      disabled={erpActionLoading}
                      className="text-xs text-rose-800 border-rose-300 hover:bg-rose-50 gap-1.5"
                      data-testid="disconnect-erp-button"
                    >
                      <Unlink className="w-3.5 h-3.5 text-rose-600" />
                      Disconnect ERP
                    </Button>
                  </div>
                </div>
              )}

              {/* State REVOKED (Phase 3 Admin Recovery) */}
              {erpStatus?.status === 'REVOKED' && (
                <div className="space-y-4" data-testid="erp-revoked-section">
                  <div className="p-4 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-900 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 font-semibold text-sm text-rose-950">
                        <ShieldAlert className="w-4 h-4 text-rose-600" />
                        OneSpace ERP Connection Disconnected
                      </div>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-rose-100 text-rose-800 border border-rose-300">
                        Disconnected / Inactive
                      </span>
                    </div>
                    <p>
                      The integration connection for this clinic has been disconnected. Automated dispensing synchronization and inventory batch sync are currently paused.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] font-mono text-rose-800 pt-1">
                      <div>Associated Request: <span className="font-bold text-rose-950">{erpStatus.requestId || 'N/A'}</span></div>
                      <div>Company ID: <span className="font-bold text-rose-950">{erpStatus.companyId || 'N/A'}</span></div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Button
                      onClick={handleInitiateErpOAuth}
                      disabled={erpActionLoading}
                      className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2 text-xs"
                      data-testid="reconnect-erp-button"
                    >
                      {erpActionLoading ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          Initiating Recovery...
                        </>
                      ) : (
                        <>
                          <RotateCcw className="h-3.5 w-3.5" />
                          Reconnect / Reauthorize OneSpace ERP
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchErpStatus(true)}
                      disabled={erpLoading || erpActionLoading}
                      className="text-xs gap-1.5"
                    >
                      <RefreshCw className={`h-3.5 w-3.5 ${erpLoading ? 'animate-spin' : ''}`} />
                      Check Status
                    </Button>
                  </div>
                  <p className="text-[11px] text-slate-500">
                    Re-authorizes connection permissions to reactivate credentials without requiring a new connection request.
                  </p>
                </div>
              )}

              {/* State REJECTED */}
              {erpStatus?.status === 'REJECTED' && (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-xs text-red-900 space-y-1.5">
                    <div className="flex items-center gap-2 font-semibold text-sm text-red-950">
                      <AlertCircle className="w-4 h-4 text-red-600" />
                      Integration Request Not Approved
                    </div>
                    <p>
                      The OneSpace Platform Administrator could not approve this request at this time.
                    </p>
                    {erpStatus.rejectionReason && (
                      <p className="text-red-800 bg-white/60 p-2 rounded border border-red-200 font-mono text-[11px] mt-1">
                        Reason: {erpStatus.rejectionReason}
                      </p>
                    )}
                  </div>
                  <div>
                    <Button
                      onClick={handleResetErpConnection}
                      disabled={erpActionLoading}
                      variant="outline"
                      className="text-xs gap-1.5"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Request Again
                    </Button>
                  </div>
                </div>
              )}

              {/* State CANCELLED */}
              {erpStatus?.status === 'CANCELLED' && (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-700 space-y-1">
                    <p className="font-semibold text-slate-900">Request Cancelled</p>
                    <p>The previous integration request was cancelled by the administrator.</p>
                  </div>
                  <div>
                    <Button
                      onClick={handleResetErpConnection}
                      disabled={erpActionLoading}
                      variant="outline"
                      className="text-xs gap-1.5"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      New Request
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Prescription Completion Preferences Card */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-4 border-b bg-slate-50/50">
              <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-emerald-600" />
                Prescription Completion Preferences
              </CardTitle>
              <CardDescription className="text-xs text-slate-500">
                Configure default payment behavior when fulfilling prescriptions.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              <div className="space-y-2 max-w-md">
                <label
                  htmlFor="default-payment-mode"
                  className="text-xs font-semibold uppercase tracking-wide text-slate-700 block"
                >
                  Default Payment Mode
                </label>
                <Select
                  value={selectedMode}
                  onValueChange={setSelectedMode}
                >
                  <SelectTrigger
                    id="default-payment-mode"
                    data-testid="default-payment-mode-select"
                    className="h-10 bg-white"
                  >
                    <SelectValue placeholder="Select default payment mode" />
                  </SelectTrigger>
                  <SelectContent>
                    {ALLOWED_PAYMENT_MODES.map((mode) => (
                      <SelectItem key={mode.value} value={mode.value}>
                        {mode.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 leading-relaxed pt-1">
                  Preselects this payment mode when an Administrator completes a prescription. The Admin can still override it for individual prescriptions.
                </p>
              </div>

              {/* Informational callout regarding staff role boundaries */}
              <div className="rounded-lg border border-amber-200 bg-amber-50/70 p-3.5 text-xs text-amber-800 space-y-1">
                <div className="font-semibold flex items-center gap-1.5 text-amber-900">
                  <AlertCircle className="h-4 w-4 text-amber-700" />
                  Role Separation & Staff Policy
                </div>
                <p className="text-amber-800 leading-relaxed">
                  Operational staff (pharmacists and clinic staff) are strictly required to select a payment mode for every prescription fulfillment. Operational staff accounts never inherit or fall back to this Admin default.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Prescription Billing & ERP Integration Card */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-4 border-b bg-slate-50/50">
              <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <Receipt className="h-5 w-5 text-emerald-600" />
                Prescription Billing & ERP Integration
              </CardTitle>
              <CardDescription className="text-xs text-slate-500">
                Configure tax override behavior and global prescription discount percentages for OneSpace ERP sales.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              {/* Override ERP Tax Setting */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-lg border border-slate-200 bg-slate-50/50">
                <div className="space-y-1">
                  <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                    Override ERP Tax
                    {overrideErpTax ? (
                      <Badge variant="outline" className="text-xs font-normal border-emerald-300 bg-emerald-50 text-emerald-700">
                        Actual 0% Tax (Override ON)
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs font-normal border-blue-300 bg-blue-50 text-blue-700">
                        Use ERP Product GST/Tax (Override OFF)
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 max-w-lg leading-relaxed">
                    When enabled (ON), prescription sales created in OneSpace ERP force actual 0% tax for every product without creating unintended offset discounts.
                    When disabled (OFF), each medicine in the prescription sale respects its catalog-configured GST/tax rate from OneSpace ERP.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="override-erp-tax-switch"
                    data-testid="override-erp-tax-switch"
                    checked={overrideErpTax}
                    onCheckedChange={setOverrideErpTax}
                  />
                </div>
              </div>

              {/* Prescription Discount Setting */}
              <div className="space-y-2 max-w-md">
                <label
                  htmlFor="prescription-discount-input"
                  className="text-xs font-semibold uppercase tracking-wide text-slate-700 flex items-center gap-1.5"
                >
                  <Percent className="h-3.5 w-3.5 text-slate-500" />
                  Prescription Discount (%)
                </label>
                <div className="relative">
                  <Input
                    id="prescription-discount-input"
                    data-testid="prescription-discount-input"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={prescriptionDiscount}
                    onChange={handleDiscountChange}
                    className={`h-10 bg-white pr-8 ${discountError ? 'border-red-400 focus-visible:ring-red-300' : ''}`}
                    placeholder="0.00"
                  />
                  <span className="absolute right-3 top-2.5 text-xs text-slate-400 font-medium">%</span>
                </div>
                {discountError ? (
                  <p className="text-xs text-red-600 font-medium" data-testid="discount-error-msg">{discountError}</p>
                ) : (
                  <p className="text-xs text-slate-500 leading-relaxed pt-1">
                    Applies a uniform line-item discount to every medicine in the prescription when creating sales in OneSpace ERP. Enter 0 for no discount.
                  </p>
                )}
              </div>

              {lastUpdated && (
                <p className="text-[11px] text-slate-400 italic pt-2 border-t">
                  Last updated by {lastUpdated.by} on {new Date(lastUpdated.at).toLocaleString()}
                </p>
              )}

              {/* Action Buttons */}
              <div className="flex items-center gap-3 pt-2">
                <Button
                  onClick={handleSave}
                  disabled={saving || !isDirty || Boolean(discountError)}
                  data-testid="save-settings-btn"
                  className="bg-emerald-700 hover:bg-emerald-800 text-white gap-1.5"
                >
                  <Save className="h-4 w-4" />
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
                {isDirty && (
                  <Button
                    variant="outline"
                    onClick={handleReset}
                    disabled={saving}
                    data-testid="reset-settings-btn"
                    className="text-slate-600 gap-1.5"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Discard Changes
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Disconnect Modal */}
      {disconnectModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 border border-slate-200 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b pb-3">
              <h3 className="font-bold text-base text-rose-600 flex items-center gap-2">
                <ShieldAlert className="w-5 h-5" />
                Disconnect OneSpace ERP
              </h3>
              <button
                onClick={() => setDisconnectModalOpen(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-800 space-y-1">
              <div className="font-bold">Deactivation Warning</div>
              <p>
                Disconnecting will immediately revoke AyurClinic's integration credentials in OneSpace ERP. Further real-time dispensing sales and inventory sync will be paused.
              </p>
            </div>

            <form onSubmit={handleDisconnectErp} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Reason for Disconnecting
                </label>
                <Input
                  type="text"
                  value={disconnectReason}
                  onChange={(e) => setDisconnectReason(e.target.value)}
                  placeholder="e.g. Scheduled migration, clinic maintenance..."
                  className="text-xs h-9"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setDisconnectModalOpen(false)}
                  disabled={erpActionLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="destructive"
                  size="sm"
                  disabled={erpActionLoading}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                >
                  {erpActionLoading ? 'Disconnecting...' : 'Confirm Disconnect'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Rotate Modal */}
      {rotateModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 border border-slate-200 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b pb-3">
              <h3 className="font-bold text-base text-amber-600 flex items-center gap-2">
                <Key className="w-5 h-5" />
                Rotate Credentials
              </h3>
              <button
                onClick={() => setRotateModalOpen(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800 space-y-1">
              <div className="font-bold">Seamless Credential Rotation</div>
              <p>
                Generates a new cryptographically secure secret on OneSpace ERP, securely encrypts it locally with AES-256-GCM, and updates database records with zero service interruption.
              </p>
            </div>

            <form onSubmit={handleRotateErpCredentials} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Rotation Note / Reason
                </label>
                <Input
                  type="text"
                  value={rotateReason}
                  onChange={(e) => setRotateReason(e.target.value)}
                  placeholder="e.g. Quarterly key rotation policy..."
                  className="text-xs h-9"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setRotateModalOpen(false)}
                  disabled={erpActionLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={erpActionLoading}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  {erpActionLoading ? 'Rotating...' : 'Confirm & Rotate'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettings;
